import {
  collection,
  getDocs,
  limit as fsLimit,
  onSnapshot,
  orderBy,
  query,
  where,
  type QuerySnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import {
  TenantLocaleSchema,
  TenantSchema,
  type Tenant,
  type TenantStatus,
} from '@etiquetachef/types';
import { firestore } from './firebase';
import { snapshotData } from './firestoreNormalize';
import { trackEvents } from './events';

const TAG = 'admin/tenants';

// B-21 fechou `locale` numa lista de 9 valores. Tenants criados antes
// disso trazem locale BCP-47 livre (`pt`, `en-US`, `it-IT`, …) que o Zod
// recusa — derrubando o doc inteiro e sumindo da listagem. Coage o legado
// para o canônico mais próximo pela língua antes do parse (mesma filosofia
// do resto do `normalizeTenantData`: destrava a UI sem migrar o doc).
const CANONICAL_LOCALES = new Set<string>(TenantLocaleSchema.options);

/**
 * Coage `locale` legado (BCP-47 livre) para um valor do `TenantLocaleSchema`.
 *
 * Origem: sem issue — commit 68eb89e98, 2026-06-09 (/tenants exibe todos os
 * tenants: locale legado + Timestamp em `firstLabelAt`).
 *
 * Contrato: não-string passa intacto; valor do enum (após normalizar a caixa)
 * passa; senão mapeia pela língua-base (`pt*` → pt-BR, `en*` → en, `es*` →
 * es, `it*` → it). Língua sem caso devolve o valor original para o Zod recusar.
 *
 * Usado por: `normalizeTenantData` (aqui) e `lib/firestoreTenantDetail.ts`.
 *
 * Falhas conhecidas:
 *  - Não há caso para `fr`: `fr` puro passa pelo enum, mas um legado `fr-FR`
 *    ou `fr-CA` é recusado e o tenant some da lista (com
 *    `tenant_doc_dropped`) — achado da #926.
 */
export function normalizeLocale(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const [langRaw = '', regionRaw] = value.trim().split('-');
  const lang = langRaw.toLowerCase();
  // Normaliza caixa BCP-47 (idioma minúsculo, região maiúscula) e tenta o enum.
  const canon = regionRaw ? `${lang}-${regionRaw.toUpperCase()}` : lang;
  if (CANONICAL_LOCALES.has(canon)) return canon;
  // Fora do enum: mapeia pela língua-base. `pt` nu / variantes não-PT → pt-BR.
  switch (lang) {
    case 'pt':
      return 'pt-BR';
    case 'en':
      return 'en';
    case 'es':
      return 'es';
    case 'it':
      return 'it';
    default:
      return value; // língua não suportada: deixa o Zod recusar (e reportar)
  }
}

// Limite "duro" do MVP. A coleção `/tenants` ainda não tem campo
// `searchKeywords` denormalizado, então a busca textual é client-side.
// Com 200 docs já cabem todos os tenants em memória sem custo perceptível;
// se passar disso, a UI mostra um aviso ("truncated") até a Sprint 4
// adicionar índice + paginação cursor-based.
export const TENANTS_LIST_LIMIT = 200;

/** Página da listagem; `truncated` = bateu em `TENANTS_LIST_LIMIT`. */
export type TenantListResult = {
  tenants: Tenant[];
  /** true quando a query bateu no limite — sinaliza que pode haver mais. */
  truncated: boolean;
};

/** Filtros server-side da listagem. */
export type TenantListFilters = {
  /** Filtro server-side. `null` = qualquer status. */
  status: TenantStatus | null;
};

// Tenants legados trazem `active: boolean` (pré-`status`) e campos de tempo
// (`createdAt`, `statusChangedAt`, `firstLabelAt`, `deletedAt`, …) como
// Firestore `Timestamp` em vez de millis. `snapshotData` (B-24) converte TODO
// Timestamp recursivamente — a conversão inline anterior cobria só
// `createdAt`/`statusChangedAt`, então qualquer doc com `firstLabelAt`
// Timestamp era recusado pelo Zod (`expected number, received Timestamp`) e
// sumia da lista (4 de 6 tenants em prod). Aqui só resta o fallback
// `active → status` e a normalização do `locale` legado (B-21).
/**
 * Dados do doc de tenant prontos para o `TenantSchema` (Timestamp → ms,
 * `active` legado → `status`, `locale` legado → canônico, `id` do caminho).
 *
 * Origem: sem issue — commit afdae1053, 2026-04-26 (tolera Timestamp e
 * legado `active`).
 *
 * Usado por: `parseSnapshot` (listagem e demos) e `lib/firestoreTenantDetail.ts`
 * (detalhe do tenant).
 *
 * Arquitetura: destrava a UI sem migrar o doc — o servidor continua vendo o
 * dado cru. Não gravar de volta o resultado daqui.
 */
export function normalizeTenantData(doc: QuerySnapshot['docs'][number]): Record<string, unknown> {
  const out: Record<string, unknown> = { ...snapshotData(doc), id: doc.id };
  if (out.status === undefined && out.active === true) {
    out.status = 'ACTIVE';
  }
  out.locale = normalizeLocale(out.locale);
  return out;
}

/** Parse da query: válidos entram; inválidos saem com `tenant_doc_dropped` no PostHog. */
function parseSnapshot(snap: QuerySnapshot): TenantListResult {
  const tenants: Tenant[] = [];
  for (const doc of snap.docs) {
    const raw = normalizeTenantData(doc);
    const parsed = TenantSchema.safeParse(raw);
    if (parsed.success) {
      tenants.push(parsed.data);
    } else {
      // Drop silencioso = tenant some da lista sem ninguém ver (não temos
      // DevTools do super-admin em prod). console.warn não basta: reporta
      // formal pro PostHog para flagrar docs legados/corrompidos.
      console.warn(`${TAG}: doc ${doc.id} ignorado — Zod inválido`, parsed.error.issues);
      const fields = [...new Set(parsed.error.issues.map((i) => i.path.join('.')))].join(',');
      trackEvents.tenantDocDropped(doc.id, fields);
    }
  }
  return { tenants, truncated: snap.docs.length >= TENANTS_LIST_LIMIT };
}

/**
 * Subscriber real-time da listagem de tenants. Devolve `Unsubscribe` para
 * limpeza do listener no useEffect. O super-admin tem read autorizado por
 * `firestore.rules` (`isSuperAdmin()`), então a mesma query roda igual.
 *
 * Filtros adicionais (planId, nextDueAt) ficam fora porque vivem em
 * sub-doc `billing/current` — Sprint 4 desnormaliza para tenant root.
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26 (lista de tenants com
 * filtro + busca + listener real-time).
 *
 * Contrato: `createdAt desc`, até `TENANTS_LIST_LIMIT`; com `status`, filtra
 * no servidor. Doc inválido é reportado (`tenant_doc_dropped`) e pulado;
 * erro de snapshot vai para `onError`.
 *
 * Usado por: `features/tenants/useTenants.ts` (tela `/tenants`, spec
 * `docs/screens/tenants-list-admin.md`) e `features/countries/CountriesTab.tsx`.
 *
 * Arquitetura: rule `match /tenants/{tenantId}` (super-admin lê tudo). Índice
 * composto `tenants (status ASC, createdAt DESC)` em
 * `firebase/firestore.indexes.json`; sem status, `orderBy` de campo único.
 *
 * Dados: `tenants/{tenantId}` — `TenantSchema` (`name`, `status`,
 * `countryCode`, `locale`, `createdAt`, `isDemo`, `partner_id`, …).
 *
 * Dicas: o teto de 200 é do MVP (busca é client-side, sem `searchKeywords`);
 * passar disso exige paginação por cursor, não só subir o número.
 */
export function subscribeTenants(
  filters: TenantListFilters,
  onResult: (result: TenantListResult) => void,
  onError: (err: unknown) => void,
): Unsubscribe {
  console.info(`${TAG}: subscribe status=${filters.status ?? '<any>'}`);
  const tenantsRef = collection(firestore, 'tenants');
  const q =
    filters.status === null
      ? query(tenantsRef, orderBy('createdAt', 'desc'), fsLimit(TENANTS_LIST_LIMIT))
      : query(
          tenantsRef,
          where('status', '==', filters.status),
          orderBy('createdAt', 'desc'),
          fsLimit(TENANTS_LIST_LIMIT),
        );
  return onSnapshot(
    q,
    (snap) => {
      onResult(parseSnapshot(snap));
    },
    (err) => {
      console.error(`${TAG}: snapshot error`, err);
      onError(err);
    },
  );
}

/** Resultado de `listDemoTenants`: demos válidos + todos os ids `isDemo`. */
export type DemoTenantList = {
  /** Demos que passam no `TenantSchema`, ordenados por nome — o que a UI lista. */
  tenants: Tenant[];
  /** Todo doc com `isDemo == true`, inclusive o que falhou no parse. */
  demoIds: ReadonlySet<string>;
};

/**
 * Lista os tenants de demonstração (`isDemo === true`), ordenados por nome.
 *
 * Origem: #785 — 2026-10-01 13:52 +0200 (demos liberados à mão na integração).
 *
 * Por quê: o editor de integração da API mostra os demos para o operador
 * liberar empresas de teste a um parceiro. Leitura única (não listener): o
 * modal lê ao abrir, e um demo criado com ele aberto aparece na próxima.
 *
 * Contrato: `where('isDemo', '==', true)` — igualdade em campo único, atendida
 * pelo índice automático (sem entrada em `firestore.indexes.json`). Ordena em
 * memória para não exigir índice composto. Doc que falha no `TenantSchema`
 * sai de `tenants` e é reportado pelo `parseSnapshot` (mesmo caminho da
 * listagem), mas o id dele continua em `demoIds`.
 * Rules: super-admin lê `/tenants`.
 *
 * Usado por: `ApiKeyEditorDialog` (`/settings/api-keys`).
 *
 * Dados: `tenants/{id}` — usa `name`, `countryCode`, `partner_id` e `isDemo`
 * (escrito por `setTenantDemoFlag`, #275). Schema: `TenantSchema`.
 *
 * Falhas conhecidas:
 *  - 2026-10-01 14:14 +0200 (#785, auditoria): devolver só os docs que passam
 *    no Zod fazia o editor tratar um demo de doc legado como "deixou de ser
 *    demo" e travar o salvamento, embora o servidor (que lê `isDemo` cru)
 *    aceitasse. Por isso `demoIds` vem da query, não do parse.
 *
 * Issues: #785, #275.
 */
export async function listDemoTenants(): Promise<DemoTenantList> {
  const snap = await getDocs(query(collection(firestore, 'tenants'), where('isDemo', '==', true)));
  return {
    tenants: parseSnapshot(snap).tenants.sort((a, b) => a.name.localeCompare(b.name)),
    demoIds: new Set(snap.docs.map((doc) => doc.id)),
  };
}

/**
 * Busca textual client-side por nome, id ou `taxId` (case-insensitive).
 *
 * Origem: sem issue — commit baefca8cc, 2026-04-26.
 *
 * Usado por: `features/tenants/TenantPickerTable.tsx` e
 * `features/notifications/BroadcastHistory.tsx`.
 */
export function matchesSearch(tenant: Tenant, searchTerm: string): boolean {
  if (searchTerm === '') return true;
  const needle = searchTerm.trim().toLowerCase();
  if (needle === '') return true;
  return (
    tenant.name.toLowerCase().includes(needle) ||
    tenant.id.toLowerCase().includes(needle) ||
    (tenant.taxId?.toLowerCase().includes(needle) ?? false)
  );
}
